import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page } from "@/components/Bits";
import { ModulesPanel } from "@/components/ModulesPanel";
import { useModules } from "@/context/ModulesContext";
import {
  PlugsConnected, Buildings, ListChecks, DownloadSimple, CheckCircle, Circle,
  LinkSimple, ArrowRight, ArrowClockwise, RocketLaunch, Sparkle, CircleNotch, Stack,
} from "@phosphor-icons/react";

const STEPS = [
  { key: "connect", label: "Connect Xero", icon: PlugsConnected },
  { key: "select", label: "Choose organisation", icon: Buildings },
  { key: "modules", label: "Choose modules", icon: Stack },
  { key: "codes", label: "Tag nominal codes", icon: ListChecks },
  { key: "import", label: "Import & finish", icon: DownloadSimple },
];
const ORDER = STEPS.map((s) => s.key);

const ROLES = [
  { key: "prepay", label: "Prepayment", set: "prepayment_codes" },
  { key: "accrual", label: "Accrual", set: "accrual_codes" },
  { key: "deferred", label: "Deferred income", set: "deferred_income_codes" },
];

function Stepper({ current, done }) {
  return (
    <div className="flex items-center gap-2 mb-8" data-testid="onboarding-stepper">
      {STEPS.map((s, i) => {
        const idx = ORDER.indexOf(s.key);
        const curIdx = done ? STEPS.length : ORDER.indexOf(current);
        const isDone = idx < curIdx;
        const isActive = s.key === current && !done;
        return (
          <div key={s.key} className="flex items-center gap-2 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                isDone ? "bg-positive/15" : isActive ? "bg-orange/15" : "bg-slate-100"}`}>
                {isDone ? <CheckCircle size={18} weight="fill" className="text-positive" style={{ color: "#00A859" }} />
                  : <s.icon size={17} weight="fill" className={isActive ? "text-orange" : "text-slate-400"} style={isActive ? { color: "#F37A24" } : undefined} />}
              </span>
              <span className={`text-xs font-semibold hidden sm:block truncate ${isActive ? "text-navy" : "text-navy/50"}`}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <span className={`h-0.5 flex-1 rounded ${idx < curIdx ? "bg-positive/40" : "bg-slate-100"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function Shell({ icon: Icon, title, sub, children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="card-surface p-8">
      <div className="flex items-start gap-4 mb-5">
        <span className="w-12 h-12 rounded-2xl bg-navy/[0.06] flex items-center justify-center shrink-0">
          <Icon size={24} weight="fill" className="text-navy" />
        </span>
        <div>
          <h2 className="font-heading font-black text-navy text-2xl tracking-tight">{title}</h2>
          <p className="text-navy/70 text-sm mt-1">{sub}</p>
        </div>
      </div>
      {children}
    </motion.div>
  );
}

export default function Onboarding() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/onboarding/status");
    setStatus(data);
    return data;
  }, []);

  useEffect(() => { load().catch(() => setStatus({ step: "connect" })); }, [load]);

  const step = status?.step;

  if (!status) return <Page><span className="font-mono text-slate-600 text-sm tracking-widest">LOADING…</span></Page>;

  return (
    <Page>
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-1.5 text-xs font-bold text-orange bg-orange/10 px-3 py-1 rounded-full mb-3" style={{ color: "#F37A24" }}>
            <Sparkle size={13} weight="fill" /> GETTING STARTED
          </div>
          <h1 className="font-heading font-black text-navy text-3xl sm:text-4xl tracking-tight">Set up WorkWithLottie</h1>
          <p className="text-navy/70 text-sm mt-2">Four quick steps to a live accruals &amp; prepayments register.</p>
        </div>

        <Stepper current={step} done={status.complete} />

        {step === "connect" && <ConnectStep onNext={load} busy={busy} setBusy={setBusy} />}
        {step === "select" && <SelectStep status={status} onNext={load} busy={busy} setBusy={setBusy} />}
        {step === "modules" && <ModulesStep onNext={load} busy={busy} setBusy={setBusy} />}
        {step === "codes" && <CodesStep onNext={load} busy={busy} setBusy={setBusy} />}
        {step === "import" && <ImportStep onNext={load} busy={busy} setBusy={setBusy} />}
        {status.complete && <DoneStep nav={nav} />}
      </div>
    </Page>
  );
}

function ConnectStep({ onNext, busy, setBusy }) {
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/xero/connect");
      const url = data.mocked && data.auth_url_relative
        ? `${window.location.origin}${data.auth_url_relative}` : data.auth_url;
      const win = window.open(url, "_blank");
      if (win && !win.closed) { win.focus(); toast.info("Continue in the new Xero tab, then return and press Refresh."); }
      else { try { window.top.location.href = url; } catch { window.location.href = url; } }
    } catch { toast.error("Could not start the Xero connection"); }
    finally { setBusy(false); }
  };
  return (
    <Shell icon={PlugsConnected} title="Connect your Xero organisation"
      sub="Lottie reads your ledger securely over Xero's official OAuth — she never stores your Xero password.">
      <div className="flex flex-wrap gap-3">
        <button onClick={connect} disabled={busy} data-testid="onboarding-connect-btn"
          className="inline-flex items-center gap-2 bg-orange text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-orangedark transition-colors disabled:opacity-50"
          style={{ background: "#F37A24" }}>
          <LinkSimple size={17} weight="bold" /> {busy ? "Connecting…" : "Connect to Xero"}
        </button>
        <button onClick={onNext} data-testid="onboarding-connect-refresh"
          className="inline-flex items-center gap-2 border border-navy/20 text-navy px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-navy/[0.04] transition-colors">
          <ArrowClockwise size={16} weight="bold" /> I've connected — refresh
        </button>
      </div>
    </Shell>
  );
}

function SelectStep({ status, onNext, busy, setBusy }) {
  const [orgs, setOrgs] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    api.get("/xero/status").then(({ data }) => {
      setOrgs(data.organisations || []);
      const cur = (data.organisations || []).find((o) => o.active) || (data.organisations || []).find((o) => o.selected);
      setActive(cur?.tenant_id || (data.organisations || [])[0]?.tenant_id || null);
    }).catch(() => {});
  }, []);
  const cont = async () => {
    if (!active) { toast.error("Pick an organisation to manage"); return; }
    setBusy(true);
    try {
      await api.post("/xero/organisations/select", { tenant_ids: [active] });
      await api.post("/xero/organisations/activate", { tenant_id: active });
      toast.success("Organisation selected");
      await onNext();
    } catch { toast.error("Could not save your organisation choice"); }
    finally { setBusy(false); }
  };
  return (
    <Shell icon={Buildings} title="Choose the organisation to manage"
      sub="Pick the Xero organisation you want Lottie to automate first — you can add more later.">
      <div className="space-y-2 mb-5" data-testid="onboarding-org-list">
        {orgs.length === 0 && <p className="text-sm text-navy/60">No organisations found yet. Go back and reconnect Xero.</p>}
        {orgs.map((o) => (
          <button key={o.tenant_id} onClick={() => setActive(o.tenant_id)} data-testid={`onboarding-org-${o.tenant_id}`}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
              active === o.tenant_id ? "border-orange bg-orange/[0.05]" : "border-slate-200 hover:border-navy/30"}`}>
            {active === o.tenant_id ? <CheckCircle size={20} weight="fill" style={{ color: "#F37A24" }} /> : <Circle size={20} className="text-slate-300" />}
            <span className="font-semibold text-navy text-sm">{o.tenant_name || o.name || o.tenant_id}</span>
            {o.demo && <span className="text-[0.65rem] font-bold text-navy/50 bg-slate-100 px-2 py-0.5 rounded-full ml-auto">DEMO</span>}
          </button>
        ))}
      </div>
      <button onClick={cont} disabled={busy || !active} data-testid="onboarding-select-continue"
        className="inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
        style={{ background: "#0E103F" }}>
        {busy ? <CircleNotch size={16} className="animate-spin" /> : <ArrowRight size={16} weight="bold" />} Continue
      </button>
    </Shell>
  );
}

function ModulesStep({ onNext, busy, setBusy }) {
  const { modules, refresh } = useModules();
  const [showHelp, setShowHelp] = useState(false);
  useEffect(() => { refresh(); }, [refresh]);
  const anyOn = modules && Object.values(modules).some(Boolean);
  const cont = async () => {
    if (!anyOn) { toast.error("Switch on at least one module to continue"); return; }
    setBusy(true);
    try { await onNext(); } finally { setBusy(false); }
  };
  const GUIDE = [
    ["Prepayments", "Costs you've paid up front that cover future months — like annual insurance or a software subscription."],
    ["Accruals", "Costs you've incurred but not yet been billed for — like rent or utilities owed at month-end."],
    ["Deferred income", "Money you've received in advance and will earn over time — like a support contract paid yearly."],
    ["Fixed assets", "Equipment and other assets you depreciate on a schedule over their useful life."],
  ];
  return (
    <Shell icon={Stack} title="Choose the modules you run"
      sub="Switch on only the ledgers this organisation needs. You can turn more on later — but a module can never be switched off once it's live.">
      <div className="mb-4 rounded-xl border border-slate-200 overflow-hidden" data-testid="onboarding-modules-help">
        <button type="button" onClick={() => setShowHelp((s) => !s)} data-testid="onboarding-modules-help-toggle"
          className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-navy bg-slate-50 hover:bg-slate-100 transition-colors">
          <span className="inline-flex items-center gap-2"><Sparkle size={14} weight="fill" style={{ color: "#F37A24" }} /> Which modules do I need?</span>
          {showHelp ? <ArrowRight size={14} weight="bold" className="rotate-90 transition-transform" /> : <ArrowRight size={14} weight="bold" className="transition-transform" />}
        </button>
        {showHelp && (
          <ul className="divide-y divide-slate-100" data-testid="onboarding-modules-help-body">
            {GUIDE.map(([name, desc]) => (
              <li key={name} className="px-4 py-3">
                <span className="text-sm font-semibold text-navy">{name}</span>
                <span className="text-sm text-navy/70"> — {desc}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <ModulesPanel onChange={refresh} />
      <div className="flex items-center gap-3 mt-6">
        <button onClick={cont} disabled={busy || !anyOn} data-testid="onboarding-modules-continue"
          className="inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
          style={{ background: "#0E103F" }}>
          {busy ? <CircleNotch size={16} className="animate-spin" /> : <ArrowRight size={16} weight="bold" />} Continue
        </button>
        <span className="text-xs text-navy/60">{anyOn ? "You can switch on more modules whenever you need them." : "Switch on at least one module to continue."}</span>
      </div>
    </Shell>
  );
}

function CodesStep({ onNext, busy, setBusy }) {
  const [accounts, setAccounts] = useState([]);
  const [roles, setRoles] = useState({}); // code -> role key
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([api.get("/accounts"), api.get("/settings")]).then(([a, s]) => {
      setAccounts(a.data.accounts || []);
      const r = {};
      (s.data.settings.prepayment_codes || []).forEach((c) => (r[c] = "prepay"));
      (s.data.settings.accrual_codes || []).forEach((c) => (r[c] = "accrual"));
      (s.data.settings.deferred_income_codes || []).forEach((c) => (r[c] = "deferred"));
      setRoles(r);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const candidates = useMemo(
    () => accounts.filter((a) => a.class === "ASSET" || a.class === "LIABILITY"),
    [accounts]);
  const chosen = Object.values(roles).filter(Boolean).length;

  const save = async () => {
    if (!chosen) { toast.error("Tag at least one nominal code to monitor"); return; }
    setBusy(true);
    const payload = { prepayment_codes: [], accrual_codes: [], deferred_income_codes: [] };
    Object.entries(roles).forEach(([code, role]) => {
      const r = ROLES.find((x) => x.key === role);
      if (r) payload[r.set].push(code);
    });
    try {
      await api.put("/settings", payload);
      toast.success("Nominal codes tagged");
      await onNext();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save your codes"); }
    finally { setBusy(false); }
  };

  return (
    <Shell icon={ListChecks} title="Tag your nominal codes"
      sub="Tell Lottie which balance-sheet codes hold your prepayments, accruals and deferred income. She'll import the matching items automatically.">
      {loading ? <p className="text-sm text-navy/60 py-6">Loading your chart of accounts…</p> : (
        <>
          <div className="max-h-[320px] overflow-y-auto rounded-xl border border-slate-100 divide-y divide-slate-50 mb-4" data-testid="onboarding-codes-list">
            {candidates.length === 0 && <p className="text-sm text-navy/60 p-4">No asset/liability codes found in this organisation's chart.</p>}
            {candidates.map((a) => (
              <div key={a.code} className="flex items-center gap-3 px-4 py-2.5" data-testid={`onboarding-code-${a.code}`}>
                <div className="min-w-0 flex-1">
                  <span className="font-mono text-sm font-semibold text-navy">{a.code}</span>
                  <span className="text-sm text-navy/70 ml-2 truncate">{a.name}</span>
                </div>
                <div className="flex gap-1 shrink-0">
                  {ROLES.map((r) => (
                    <button key={r.key} onClick={() => setRoles((p) => ({ ...p, [a.code]: p[a.code] === r.key ? undefined : r.key }))}
                      data-testid={`onboarding-role-${a.code}-${r.key}`}
                      className={`text-[0.7rem] font-semibold px-2.5 py-1 rounded-lg border transition-colors ${
                        roles[a.code] === r.key ? "border-orange bg-orange/10 text-orangeink" : "border-slate-200 text-navy/50 hover:border-navy/30"}`}
                      style={roles[a.code] === r.key ? { borderColor: "#F37A24", color: "#B4560F" } : undefined}>
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={save} disabled={busy} data-testid="onboarding-codes-save"
              className="inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ background: "#0E103F" }}>
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <ArrowRight size={16} weight="bold" />} Save & continue
            </button>
            <span className="text-xs text-navy/60">{chosen} code{chosen === 1 ? "" : "s"} tagged</span>
          </div>
        </>
      )}
    </Shell>
  );
}

function ImportStep({ onNext, busy, setBusy }) {
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [copied, setCopied] = useState(false);

  const pollJob = useCallback(async (jobId) => {
    let imported = 0;
    for (let i = 0; i < 480; i++) { // up to ~20 min at 2.5s intervals
      await new Promise((r) => setTimeout(r, 2500));
      let job;
      try { ({ data: job } = await api.get(`/import/jobs/${jobId}`)); } catch { continue; }
      setProgress(job.imported || 0);
      if (job.status === "done") { imported = job.imported || 0; return { done: true, imported }; }
      if (job.status === "error") { throw new Error(job.error || "Import failed"); }
    }
    throw new Error("Import is taking longer than expected. It may still finish in the background — please refresh shortly.");
  }, []);

  // Resume a background import that was already running when the tab was closed/reloaded.
  useEffect(() => {
    (async () => {
      try {
        const { data: job } = await api.get("/import/jobs/latest");
        if (job && job.status === "running" && job.id) {
          setBusy(true); setProgress(job.imported || 0);
          const r = await pollJob(job.id);
          toast.success(`Imported ${r.imported} item(s) from Xero`);
          await onNext();
        } else if (job && job.status === "error" && job.error) {
          setErrorMsg(job.error);
        }
      } catch { /* nothing to resume */ }
      finally { setBusy(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runImport = async () => {
    setBusy(true); setResult(null); setProgress(null); setErrorMsg(null);
    try {
      // Always import in the BACKGROUND and poll — a real Xero org can have a lot of history and a
      // synchronous import would exceed the request timeout.
      const { data } = await api.post("/import/run", { mode: "background" });
      if (data.background && data.job_id) {
        const r = await pollJob(data.job_id);
        setResult({ imported: r.imported });
        toast.success(`Imported ${r.imported} item(s) from Xero`);
        await onNext();
      } else {
        setResult(data);
        toast.success(`Imported ${data.imported ?? 0} item(s) from Xero`);
        await onNext();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Import failed — check your codes";
      setErrorMsg(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const copyError = () => {
    navigator.clipboard?.writeText(`WorkWithLottie onboarding import error:\n${errorMsg}`).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <Shell icon={DownloadSimple} title="Import from Xero"
      sub="Lottie will pull every invoice and bill posted to your tagged codes and build your live register.">
      <button onClick={runImport} disabled={busy} data-testid="onboarding-import-btn"
        className="inline-flex items-center gap-2 bg-orange text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-orangedark transition-colors disabled:opacity-50"
        style={{ background: "#F37A24" }}>
        {busy ? <CircleNotch size={16} className="animate-spin" /> : <DownloadSimple size={17} weight="bold" />} {busy ? "Importing…" : (errorMsg ? "Try import again" : "Import now")}
      </button>
      {busy && (
        <p className="text-sm text-navy/70 mt-4" data-testid="onboarding-import-progress">
          Pulling your invoices &amp; bills from Xero{progress != null ? ` — ${progress} item(s) so far` : ""}… this can take a minute for larger organisations, please keep this tab open.
        </p>
      )}
      {!busy && errorMsg && (
        <div className="mt-4 text-left border border-negative/30 bg-negative/[0.04] rounded-xl p-4" data-testid="onboarding-import-error">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-sm font-semibold text-negative">The import couldn't finish</div>
            <button onClick={copyError} data-testid="onboarding-copy-error"
              className="text-xs font-medium px-2.5 py-1 rounded-lg border border-slate-200 text-navy hover:border-navy transition-colors">
              {copied ? "Copied ✓" : "Copy details"}
            </button>
          </div>
          <p className="text-xs text-navy/70 mb-2">Please try again. If it keeps happening, copy the details below and send them to support so we can help quickly.</p>
          <pre className="text-xs bg-white border border-slate-200 rounded-lg p-3 whitespace-pre-wrap break-words text-navy/80 font-mono" data-testid="onboarding-error-text">{errorMsg}</pre>
        </div>
      )}
      {!busy && result && <p className="text-sm text-navy/70 mt-4">Imported {result.imported ?? 0} item(s). Building your register…</p>}
    </Shell>
  );
}

function DoneStep({ nav }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
      className="card-surface p-10 text-center" data-testid="onboarding-done">
      <span className="w-16 h-16 rounded-3xl bg-positive/15 flex items-center justify-center mx-auto mb-5">
        <RocketLaunch size={30} weight="fill" style={{ color: "#00A859" }} />
      </span>
      <h2 className="font-heading font-black text-navy text-2xl tracking-tight">You're all set! 🎉</h2>
      <p className="text-navy/70 text-sm mt-2 max-w-md mx-auto">Your accruals &amp; prepayments register is live and tied to Xero. Explore your dashboard to see it in action.</p>
      <div className="flex flex-wrap gap-3 justify-center mt-6">
        <button onClick={() => nav("/dashboard")} data-testid="onboarding-goto-dashboard"
          className="inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
          style={{ background: "#0E103F" }}>
          Go to dashboard <ArrowRight size={16} weight="bold" />
        </button>
        <button onClick={() => nav("/prepayments")} data-testid="onboarding-goto-register"
          className="inline-flex items-center gap-2 border border-navy/20 text-navy px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-navy/[0.04] transition-colors">
          Open my register
        </button>
      </div>
    </motion.div>
  );
}
